import { isNotDefined } from '@sgme/fp';
import { Select, Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { selectCurrencyPairData } from '@/api/config/selectors.ts';
import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { selectOptionCurrency, selectOptionLegsProperties } from '@/store/option/selectors/optionSelectors.ts';
import type { HedgeType, LegId } from '@/store/option/state.ts';
import type { IntlMessageId } from '@/types/intl';

type HedgeTypeProps = {
  tileId: string;
};

type PossibleHedgeType = { code: HedgeType; name: string };

export function HedgeTypeField({ tileId }: HedgeTypeProps) {
  const intl = useIntl();
  const [checkOption] = useCheckOption();
  const legId: LegId = `${tileId}/0`;
  const {
    currentUser: { userType },
  } = useUserContext();

  const isInternalSales = userType === 'Internal Sales';

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { hedgeType, deliveryDate, isNonDeliverable } = useOptionSelector((option, state) => {
    const legProperties = selectOptionLegsProperties(option, legId);
    const { currencyPair } = selectOptionCurrency(option);
    const currencyPairData = selectCurrencyPairData(state, currencyPair);
    const isNonDeliverable = currencyPairData?.notDeliverable ?? false;
    return {
      hedgeType: option.properties.hedgeType,
      deliveryDate: legProperties?.deliveryDate,
      isNonDeliverable,
    };
  });

  const warnings: IntlMessageId[] = [];
  if (isInternalSales && hedgeType === 'Live') {
    warnings.push('option.fields.hedgeType.tooltip.automaticHedge');
  }
  if (isInternalSales && isNotDefined(deliveryDate)) {
    warnings.push('option.fields.hedgeType.tooltip.needDeliveryDate');
  }

  const handleHedgeTypeChange = (value: HedgeType) => {
    sgwtWebAnalytics?.trackEvent('Option', 'Hedge type changed', value);

    checkOption({
      tileId,
      properties: {
        hedgeType: value,
      },
    });
  };

  const possibleHedgeTypes: PossibleHedgeType[] = isNonDeliverable
    ? [
        {
          code: 'Live',
          name: intl.formatMessage({ id: 'option.fields.hedgeType.values.live' }),
        },
        {
          code: 'Ndf',
          name: intl.formatMessage({ id: 'option.fields.hedgeType.values.deltaExchNDF' }),
        },
      ]
    : [
        {
          code: 'Live',
          name: intl.formatMessage({ id: 'option.fields.hedgeType.values.live' }),
        },
        {
          code: 'Spot',
          name: intl.formatMessage({ id: 'option.fields.hedgeType.values.deltaExchSpot' }),
        },
        {
          code: 'Forward',
          name: intl.formatMessage({ id: 'option.fields.hedgeType.values.deltaExchForward' }),
        },
      ];

  return (
    <OptionFormGroup
      label={<FormattedMessage id="option.fields.hedgeType" />}
      addon={
        warnings.length > 0 && (
          <Tooltip
            position="right"
            contents={
              <div>
                {warnings.map((messageId) => (
                  <FormattedMessage tagName="div" key={messageId} id={messageId} />
                ))}
              </div>
            }
          >
            <em className="icon icon-sm lh-1 cursor-default text-warning" data-e2e="HedgeTypeInfo">
              warning
            </em>
          </Tooltip>
        )
      }
    >
      <Select<PossibleHedgeType>
        data-e2e="hedge-type-select"
        selectedItemValue={hedgeType}
        onChange={(e) => handleHedgeTypeChange(e.code)}
        className="form-select-sm"
        itemValue={(item) => item.code}
        itemToString={(item) => item.name}
        itemDisabled={(item) => {
          return (item.code === 'Forward' || item.code === 'Ndf') && isNotDefined(deliveryDate);
        }}
        itemsAsObjects={possibleHedgeTypes}
      />
    </OptionFormGroup>
  );
}
